import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';

const HeroSection = styled.section`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  padding: 5rem 2rem;
  position: relative;
  overflow: hidden;

  @media (max-width: 768px) {
    padding: 3rem 1rem;
  }
`;

/* Video background */
const VideoBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: ${({ theme }) => theme.name === 'dark'
      ? 'rgba(255,255,255,0.85)'
      : 'rgba(10, 22, 40, 0.75)'
    };
  }

  video {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const Content = styled.div`
  max-width: 900px;
  text-align: center;
  position: relative;
  z-index: 1;

  opacity: 0;
  transform: translateY(40px);
  transition: opacity 1s ease-out, transform 1s ease-out;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const SectionLabel = styled.span`
  display: inline-block;
  padding: 0.5rem 1.25rem;
  background: ${({ theme }) => theme.colors.activeColor}20;
  color: ${({ theme }) => theme.colors.activeColor};
  font-size: 0.8rem;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  border-radius: 50px;
  margin-bottom: 2rem;
`;

const Headline = styled.h1`
  font-family: ${({ theme }) => theme.colors.ffHeading};
  font-size: clamp(2.5rem, 6vw, 4rem);
  font-weight: 400;
  color: ${({ theme }) => theme.name === 'dark' ? theme.colors.headline : '#ffffff'};
  margin-bottom: 1.25rem;
  line-height: 1.15;
  letter-spacing: -0.01em;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const AccentText = styled.span`
  color: ${({ theme }) => theme.colors.activeColor};
`;

const Subheadline = styled.p`
  font-size: clamp(1.05rem, 2.5vw, 1.2rem);
  color: ${({ theme }) => theme.name === 'dark' ? theme.colors.paragraph : 'rgba(255,255,255,0.85)'};
  max-width: 680px;
  margin: 0 auto 2rem;
  line-height: 1.7;

  @media (max-width: 768px) {
    font-size: 1rem;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;

  opacity: 0;
  transform: translateY(20px);
  transition: opacity 0.8s ease-out 0.3s, transform 0.8s ease-out 0.3s;

  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
`;

const PrimaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: ${({ theme }) => theme.colors.activeColor};
  color: white;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px ${({ theme }) => theme.colors.activeColor}40;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 6px 25px ${({ theme }) => theme.colors.activeColor}50;
  }
`;

const SecondaryButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  background: transparent;
  color: ${({ theme }) => theme.name === 'dark' ? theme.colors.headline : '#ffffff'};
  font-size: 1rem;
  font-weight: 600;
  border-radius: 50px;
  text-decoration: none;
  border: 2px solid ${({ theme }) => theme.name === 'dark' ? theme.colors.borderColor : 'rgba(255,255,255,0.4)'};
  transition: all 0.3s ease;

  &:hover {
    border-color: ${({ theme }) => theme.colors.activeColor};
    color: ${({ theme }) => theme.colors.activeColor};
  }
`;

const TrustBadge = styled.p`
  margin-top: 1.5rem;
  font-size: 0.85rem;
  color: ${({ theme }) => theme.name === 'dark' ? theme.colors.subTextColor : 'rgba(255,255,255,0.6)'};
  position: relative;
  z-index: 1;

  opacity: 0;
  animation: fadeIn 0.8s ease-out 0.6s forwards;

  @keyframes fadeIn {
    to {
      opacity: 1;
    }
  }
`;

const Hero = () => {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  return (
    <HeroSection>
      <VideoBackground>
        <video
          autoPlay
          muted
          playsInline
          poster="/videos/hero-poster.jpg"
        >
          <source src="/videos/hero-bg.webm" type="video/webm" />
          <source src="/videos/hero-bg.mp4" type="video/mp4" />
        </video>
      </VideoBackground>

      <Content className={isVisible ? 'visible' : ''}>
        <SectionLabel>Automated Precision Rental Analysis</SectionLabel>
        <Headline>
          Every Listing. Every Expense.
          <br />
          <AccentText>Down to the Age of the Roof.</AccentText>
        </Headline>
        <Subheadline>
          <strong>Enter one or more ZIP codes.</strong> PropNinja analyzes every active listing using market-specific 
          rent, taxes, and insurance, then calculates CapEx by component based on the actual age and 
          condition of each property — <strong>single-family or multifamily.</strong>
          <br /><br />
          <strong>The result:</strong> real cash-on-cash returns. Not a flat 5% guess.
        </Subheadline>
      </Content>

      <ButtonGroup className={isVisible ? 'visible' : ''}>
        <PrimaryButton to="/signup">
          Get Started Free
          <span>→</span>
        </PrimaryButton>
        <SecondaryButton to="/signin">
          Sign In
        </SecondaryButton>
      </ButtonGroup>

      <TrustBadge>
        5 free analyses/month &middot; No credit card required
      </TrustBadge>
    </HeroSection>
  );
};

export default Hero;
